import { useEffect, useState } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { lovable } from "@/integrations/lovable/index";
import { supabase } from "@/integrations/supabase/client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import OnboardingWizard from "@/components/auth/OnboardingWizard";
import LandingPage from "@/components/auth/LandingPage";

export default function Auth() {
  const { session, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode"); // "signup" | "signin" | null

  const [isSignUp, setIsSignUp] = useState(mode === "signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showLoginModal, setShowLoginModal] = useState(mode === "signup" || mode === "signin");
  // When true, the onboarding wizard is showing the intent step — block auto-navigation
  const [showingIntentStep, setShowingIntentStep] = useState(false);
  const { signIn } = useAuth();
  const { toast } = useToast();

  // Auto-open modal when ?mode= is present
  useEffect(() => {
    if (mode === "signup") {
      setIsSignUp(true);
      setShowLoginModal(true);
    } else if (mode === "signin") {
      setIsSignUp(false);
      setShowLoginModal(true);
    }
  }, [mode]);

  // Check must_change_password after login
  useEffect(() => {
    if (!session?.user?.id) return;
    supabase
      .from("profiles")
      .select("must_change_password")
      .eq("id", session.user.id)
      .single()
      .then(({ data }) => {
        if (data?.must_change_password) {
          setMustChangePassword(true);
          setShowLoginModal(true);
        }
      });
  }, [session?.user?.id]);

  // Stale token clearing
  const [forceReady, setForceReady] = useState(false);
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(async () => {
      try {
        await supabase.auth.signOut({ scope: "local" });
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key && (key.startsWith("sb-") || key.includes("supabase"))) {
            localStorage.removeItem(key);
          }
        }
      } catch {
        /* ignore */
      }
      setForceReady(true);
    }, 3000);
    return () => clearTimeout(t);
  }, [loading]);

  if (loading && !forceReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (session && !mustChangePassword && !showingIntentStep) {
    const savedIntent = localStorage.getItem("safe_onboarding_intent");
    if (savedIntent) {
      localStorage.removeItem("safe_onboarding_intent");
      const intentRoutes: Record<string, string> = {
        assessment: "/",
        services: "/services",
        solutions: "/marketplace",
        advisory: "/expert-advisory",
        funding: "/testbed-hub#funding",
        vendor: "/vendor/onboarding",
      };
      const target = intentRoutes[savedIntent] || "/";
      return <Navigate to={target} replace />;
    }
    // Only force-redirect to dashboard when the user actively came here to auth
    // (?mode=signin|signup). Otherwise let them browse the public landing page
    // while staying signed in.
    if (mode === "signin" || mode === "signup") {
      return <Navigate to="/" replace />;
    }
  }

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast({ title: "Password too short", description: "Must be at least 6 characters.", variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: "Passwords do not match", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      setSubmitting(false);
      return;
    }
    await supabase.from("profiles").update({ must_change_password: false }).eq("id", session!.user.id);
    setMustChangePassword(false);
    setShowLoginModal(false);
    toast({ title: "Password updated", description: "You can now use the application." });
    setSubmitting(false);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const { error } = await signIn(email, password);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
    setSubmitting(false);
  };

  return (
    <>
      <LandingPage onSignIn={() => { setIsSignUp(false); setShowLoginModal(true); }} onSignUp={() => { setIsSignUp(true); setShowLoginModal(true); }} />

      <Dialog open={showLoginModal} onOpenChange={(open) => {
        if (!mustChangePassword) setShowLoginModal(open);
      }}>
        <DialogContent className="sm:max-w-[480px] p-8">
          {mustChangePassword ? (
            <>
              <h2 className="text-xl font-bold text-foreground">Change your password</h2>
              <p className="mt-1 text-sm text-muted-foreground mb-6">
                Your account requires a password change before you can continue.
              </p>
              <form onSubmit={handlePasswordChange} className="space-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="new-password" className="text-sm font-semibold">New password</Label>
                  <Input id="new-password" type="password" placeholder="Enter new password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirm-password" className="text-sm font-semibold">Confirm password</Label>
                  <Input id="confirm-password" type="password" placeholder="Confirm new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} className="h-11" />
                </div>
                <Button type="submit" className="w-full h-11 font-semibold" disabled={submitting}>
                  {submitting ? "Updating…" : "Update password"}
                </Button>
              </form>
            </>
          ) : isSignUp ? (
            <OnboardingWizard onBackToSignIn={() => setIsSignUp(false)} onClose={() => { setShowingIntentStep(false); setShowLoginModal(false); }} onIntentStepShown={() => setShowingIntentStep(true)} />
          ) : (
            <>
              <h2 className="text-2xl font-bold text-foreground tracking-tight">Sign In</h2>
              <p className="mt-1 text-sm text-muted-foreground mb-6">
                For institutions, experts, and ecosystem partners.
              </p>

              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-1">
                  <Label htmlFor="email" className="text-sm font-semibold">Email address</Label>
                  <Input id="email" type="email" placeholder="name@example.gov" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-10" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="password" className="text-sm font-semibold">Password</Label>
                  <Input id="password" type="password" placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="h-10" />
                </div>
                <Button type="submit" className="w-full h-10 font-semibold" disabled={submitting}>
                  {submitting ? "Please wait…" : "Sign in"}
                </Button>
              </form>

              <div className="flex items-center gap-3 my-3">
                <Separator className="flex-1" />
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">or</span>
                <Separator className="flex-1" />
              </div>

              <div className="space-y-1.5">
                <Button type="button" variant="outline" className="w-full h-10 text-sm font-medium" disabled={submitting} onClick={async () => {
                  setSubmitting(true);
                  try {
                    const { error } = await lovable.auth.signInWithOAuth("google", { redirect_uri: window.location.origin });
                    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
                  } catch {
                    toast({ title: "Sign-in failed", description: "Please try using email/password instead.", variant: "destructive" });
                  }
                  setTimeout(() => setSubmitting(false), 4000);
                }}>
                  <svg className="mr-2 h-3.5 w-3.5" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </Button>
              </div>

              <p className="mt-3 text-xs text-muted-foreground">
                New here?{" "}
                <button type="button" className="text-primary font-semibold underline underline-offset-4" onClick={() => setIsSignUp(true)}>
                  Create an account to unlock features
                </button>
              </p>

              <p className="mt-2 text-[10px] text-muted-foreground leading-relaxed">
                By signing in you agree to our{" "}
                <Link to="/terms" className="text-primary underline underline-offset-2 hover:text-primary/80">Terms</Link>,{" "}
                <Link to="/privacy" className="text-primary underline underline-offset-2 hover:text-primary/80">Privacy Policy</Link>,{" "}
                <Link to="/cookies" className="text-primary underline underline-offset-2 hover:text-primary/80">Cookies</Link>, and{" "}
                <Link to="/acceptable-use" className="text-primary underline underline-offset-2 hover:text-primary/80">Acceptable Use Policy</Link>.{" "}
                <Link to="/dpa" className="text-primary underline underline-offset-2 hover:text-primary/80">DPA</Link> available for institutional clients.
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
